// NPM
import { useRef, useState } from "react";
import { DropdownItem, Modal, ModalHeader } from "reactstrap";

// Calance
import styles from "components/NavMenu/Components/AgentFileDownloader/AgentFileDownloader.module.css";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";

export default function DownloadAgentConfiguration({
    type,
    arch,
}: {
    type: "Agent" | "Agent Configuration";
    arch?: "x86" | "x64";
}) {
    // State
    const [isModalOpen, setIsModalOpen] = useState(false);
    /** Used to cancel the agent download. Used instead of state to avoid bug from lag in
     * updating state where state was still false when it was time to ignore the fetch if
     * the user closed the modal. The ref has no delay, but state is also required to rerender
     * to show/hide the modal
     */
    const isModalOpenRef = useRef(false);

    // Hooks
    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    const downloadConfig = async (response: Response) => {
        const blob = await response.blob();
        const element = document.createElement("a");
        element.href = URL.createObjectURL(blob);
        element.download = "Agent.dll.config";
        document.body.appendChild(element);
        element.click();
        element.remove();
    };

    const getFile = async () => {
        setIsModalOpen(true);
        isModalOpenRef.current = true;

        const url =
            type === "Agent"
                ? `api/admin/agent/${arch}`
                : "api/admin/agent_configuration";
        const response = await get(url);

        if (isModalOpenRef.current) {
            if (response.ok) {
                if (type === "Agent Configuration") {
                    downloadConfig(response);
                }
                if (type === "Agent") {
                    downloadAgent(response);
                }
            } else {
                const data = await response.json();
                console.log(data);
                newAlert({
                    message: `Error downloading agent files: ${data.detail}`,
                });
            }
        }
        setIsModalOpen(false);
        isModalOpenRef.current = false;
    };

    const downloadAgent = async (response: Response) => {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        const contentDisposition = response.headers.get("content-disposition"); // Get filename from headers
        let fileName = "downloaded_file.zip"; // Fallback filename
        if (contentDisposition) {
            const parts = contentDisposition.split(";");
            const name = parts
                .find((p) => p.includes("filename"))
                ?.split("=")[1];
            if (name) fileName = name.replace(/"/g, "");
        }

        link.download = fileName; // Set the filename
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const toggle = () => {
        isModalOpenRef.current = false;
        setIsModalOpen(false);
    };

    return (
        <>
            <DropdownItem className={styles.link} onClick={getFile}>
                {type === "Agent"
                    ? `Download Agent (${arch === "x86" ? "32-bit" : "64-bit"})`
                    : "Download Agent Configuration"}
            </DropdownItem>

            <Modal
                className={styles.modal}
                isOpen={isModalOpen}
                toggle={toggle}
            >
                <ModalHeader toggle={toggle}>
                    Agent is downloading ...
                </ModalHeader>
                <div className={styles.spacer}>
                    <DimensionSpinner />
                </div>
            </Modal>
        </>
    );
}
